import React, { useEffect, useMemo, useState } from 'react';
import type { DailyData } from '../../types';
import type { WidgetSize } from '../../types/dashboard';
import StreakLineAnimation from './StreakLineAnimation';
import { computeTradeLevelStats } from '../../lib/trades/tradeLevelStats';
// using emerald tones for the win animation

interface MaxConsecutiveWinsWidgetProps {
  dailyData: DailyData[];
  size?: WidgetSize;
  /** Authoritative trade records; when provided, streaks use the trade sequence. */
  trades?: any[];
}

const VALUE_FONT_BY_SIZE: Record<WidgetSize, string> = {
  mini: 'text-5xl',
  regular: 'text-6xl',
  large: 'text-7xl',
  full: 'text-7xl',
};

export const MaxConsecutiveWinsWidget: React.FC<MaxConsecutiveWinsWidgetProps> = ({ dailyData, size = 'mini', trades }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const update = () => setIsDarkMode(root.classList.contains('dark'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const maxWins = useMemo(() => {
    if (Array.isArray(trades) && trades.length > 0) {
      return computeTradeLevelStats(trades).maxConsecutiveWins;
    }
    let maxStreak = 0;
    let currentStreak = 0;
    for (const day of dailyData) {
      if (day.pnl > 0) {
        currentStreak++;
      } else {
        maxStreak = Math.max(maxStreak, currentStreak);
        currentStreak = 0;
      }
    }
    return Math.max(maxStreak, currentStreak);
  }, [dailyData, trades]);

  const cardStyle = useMemo(() => ({
    backgroundColor: isDarkMode ? '#050f19' : '#f4f7fb',
    boxShadow: isDarkMode ? '0 20px 35px rgba(0,0,0,0.45)' : '0 25px 35px rgba(15,23,42,0.12)',
  }), [isDarkMode]);

  const overlayStyle = useMemo(() => ({
    background: isDarkMode
      ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(16,185,129,0.35) 55%, rgba(5,15,25,0.85) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(220,253,235,0.75) 65%, rgba(255,255,255,0.75) 100%)',
  }), [isDarkMode]);

  const edgeFadeStyle = useMemo(() => ({
    background: isDarkMode
      ? 'linear-gradient(90deg, #050f19 0%, rgba(5,15,25,0) 18%, rgba(5,15,25,0) 82%, #050f19 100%)'
      : 'linear-gradient(90deg, #f4f7fb 0%, rgba(244,247,251,0) 18%, rgba(244,247,251,0) 82%, #f4f7fb 100%)',
  }), [isDarkMode]);

  // Make the big number match the emerald animation: light/dark variants
  const numberColor = isDarkMode ? '#bbf7e6' : '#059669';
  const textStroke = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.35)';
  const textShadow = isDarkMode
    ? '0 18px 35px rgba(16,185,129,0.65), 0 0 24px rgba(255,255,255,0.35)'
    : '0 18px 35px rgba(16,185,129,0.5), 0 0 22px rgba(255,255,255,0.75)';
  const glowBandColor = isDarkMode ? 'rgba(16,185,129,0.45)' : 'rgba(187,247,208,0.7)';
  const headerColor = isDarkMode ? '#d6ffe8' : '#14532d';
  const hairlineColor = isDarkMode ? 'rgba(16,185,129,0.4)' : 'rgba(16,185,129,0.18)';

  return (
    <div className="relative h-full w-full rounded-2xl overflow-hidden" style={cardStyle}>
      <div className="absolute inset-0" style={overlayStyle} />
      <StreakLineAnimation variant="wins" isDarkMode={isDarkMode} />
      <div className="pointer-events-none absolute inset-0" style={edgeFadeStyle} />

      <div className="pointer-events-none absolute inset-x-0 top-0 z-20">
        <div className="px-6 pt-5 pb-2">
          <span
            className="text-[0.68rem] uppercase tracking-normal font-semibold"
            style={{ color: headerColor }}
          >
            Winning Streak
          </span>
        </div>
        <div className="mx-6 h-px" style={{ background: hairlineColor }} />
      </div>

      <div className="relative z-10 flex h-full items-center justify-center">
        <div className="relative">
          <div
            className="pointer-events-none absolute -inset-x-16 top-1/2 h-16 -translate-y-1/2 blur-[95px] opacity-90"
            style={{ background: `radial-gradient(circle, ${glowBandColor} 0%, transparent 65%)` }}
          />
          <span
            className={`relative font-extrabold leading-none tabular-nums drop-shadow-[0_15px_30px_rgba(0,0,0,0.45)] ${VALUE_FONT_BY_SIZE[size]}`}
            style={{ color: numberColor, textShadow, WebkitTextStroke: `1px ${textStroke}` }}
          >
            {maxWins}
          </span>
        </div>
      </div>
    </div>
  );
};
